import { CommonModule } from '@angular/common';
import { Component, DestroyRef, ElementRef, HostListener, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { TooltipModule } from 'primeng/tooltip';
import { filter } from 'rxjs';
import { HelpService } from '../../help/help.service';
import { LoginService } from '../../services/login.service';
import { LayoutService } from '../service/layout.service';
import { AppAlterarSenha } from './app.alterar-senha';
import { montarMenu } from './app.menu';
import { ItemMenu } from './app.menu.itens';

/** Item do menu pronto para exibir: grupo (com filhos) ou tela (com link ou ação). */
export interface NoMenu {
    rotulo: string;
    icone?: string;
    /** Posição na árvore (ex.: "1-0-3"); o prefixo identifica os grupos acima. */
    chave: string;
    link: string | null;
    filhos: NoMenu[];
    acao?: () => void;
}

/** Atraso para abrir/fechar por hover, evitando abrir ao só passar o mouse por cima. */
const ATRASO_EXPANDIR = 120;
const ATRASO_RECOLHER = 300;
const ATRASO_INTENCAO = 350;
const ATRASO_FECHAR_PAINEL = 250;
/** Um clique logo após o hover abrir o item não deve fechá-lo de novo. */
const JANELA_CLIQUE_APOS_HOVER = 600;

function linkDe(item: ItemMenu): string | null {
    const link = Array.isArray(item.routerLink) ? item.routerLink[0] : item.routerLink;
    return typeof link === 'string' ? '/' + link.replace(/^\/+/, '') : null;
}

function juntar(...acoes: (undefined | (() => void))[]): (() => void) | undefined {
    const validas = acoes.filter((acao): acao is () => void => !!acao);
    return validas.length ? () => validas.forEach(acao => acao()) : undefined;
}

export function construirNos(itens: ItemMenu[], prefixo = ''): NoMenu[] {
    return itens
        .filter(item => item.visible !== false)
        .map((item, i) => {
            const chave = prefixo ? `${prefixo}-${i}` : String(i);
            const filhos = item.items ? construirNos(item.items, chave) : [];
            const acao = item.command ? () => item.command!({ item }) : undefined;
            return { rotulo: item.label ?? '', icone: item.icon, chave, link: filhos.length ? null : linkDe(item), filhos, acao };
        });
}

/** Grupos raiz com uma única tela viram atalho direto para ela, com o ícone do grupo. */
export function nosDaBarra(itens: ItemMenu[]): NoMenu[] {
    return construirNos(itens).map(no => {
        const [unico] = no.filhos;
        return no.filhos.length === 1 && !unico.filhos.length ? { ...unico, icone: no.icone, acao: juntar(no.acao, unico.acao) } : no;
    });
}

/** Chave da tela do menu mais específica que contém a URL (ex.: /pages/simulacao/123 → Simulações). */
export function chaveDaUrl(nos: NoMenu[], url: string): string | null {
    const caminho = url.split(/[?#]/)[0].replace(/(.)\/+$/, '$1') || '/';
    let melhor: NoMenu | null = null;
    const visitar = (lista: NoMenu[]) => lista.forEach(no => {
        if (no.filhos.length) return visitar(no.filhos);
        const link = no.link;
        if (!link || (melhor && melhor.link!.length >= link.length)) return;
        if (caminho === link || (link !== '/' && caminho.startsWith(link + '/'))) melhor = no;
    });
    visitar(nos);
    return (melhor as NoMenu | null)?.chave ?? null;
}

/** Chaves dos grupos acima da chave (ex.: "1-0-3" → ["1", "1-0"]). */
function ancestrais(chave: string | null): string[] {
    if (!chave) return [];
    const partes = chave.split('-');
    return partes.slice(0, -1).map((_, i) => partes.slice(0, i + 1).join('-'));
}

@Component({
    selector: 'app-sidebar',
    standalone: true,
    imports: [CommonModule, RouterModule, TooltipModule, AppAlterarSenha],
    template: `
        <aside
            class="layout-sidebar"
            [class.layout-sidebar-expandida]="comRotulos()"
            [class.layout-sidebar-sobreposta]="expandido() && !mobile()"
            aria-label="Menu principal"
            (pointerenter)="aoEntrarNaBarra($event)"
            (pointerleave)="aoSairDaBarra($event)"
            (focusin)="aoFocarBarra()"
            (focusout)="aoDesfocarBarra($event)"
        >
            <a class="layout-sidebar-logo" routerLink="/" aria-label="LXHarvest, página inicial">
                <b class="layout-sidebar-logo-marca">LX</b><b class="layout-sidebar-rotulo">Harvest</b>
            </a>

            <nav class="layout-sidebar-menu">
                <ul class="layout-sidebar-lista">
                    @for (no of nos; track no.chave) {
                        <li>
                            @if (no.filhos.length) {
                                <button
                                    type="button"
                                    class="layout-sidebar-item"
                                    [class.ativo]="contemAtivo(no)"
                                    [class.aberto]="arvore() ? comRotulos() && estaAberto(no) : painel()?.chave === no.chave"
                                    [attr.aria-expanded]="arvore() ? comRotulos() && estaAberto(no) : painel()?.chave === no.chave"
                                    [attr.aria-controls]="arvore() ? null : 'layout-sidebar-painel'"
                                    [attr.aria-label]="comRotulos() ? null : no.rotulo"
                                    (click)="clicarGrupo(no, $event)"
                                    (pointerenter)="entrarGrupo(no, $event)"
                                    (pointerleave)="sairItem($event)"
                                >
                                    <i class="layout-sidebar-icone" [ngClass]="no.icone ?? 'pi pi-folder'" aria-hidden="true"></i>
                                    <span class="layout-sidebar-rotulo">{{ no.rotulo }}</span>
                                    <i class="pi pi-angle-down layout-sidebar-seta" aria-hidden="true"></i>
                                </button>
                                @if (arvore() && comRotulos() && estaAberto(no)) {
                                    <ng-container *ngTemplateOutlet="subarvore; context: { $implicit: no.filhos }" />
                                }
                            } @else {
                                <ng-container *ngTemplateOutlet="tela; context: { $implicit: no, raiz: true }" />
                            }
                        </li>
                    }
                </ul>
            </nav>

            <div class="layout-sidebar-rodape">
                <ul class="layout-sidebar-lista">
                    @if (help.access.login.isAutenticado()) {
                        <li>
                            <button type="button" class="layout-sidebar-item" (click)="help.open()" (pointerenter)="entrarTela($event)" aria-label="Central de Ajuda (F1)" pTooltip="Central de Ajuda (F1)" tooltipPosition="right" [tooltipDisabled]="!dicas()">
                                <i class="layout-sidebar-icone pi pi-question-circle" aria-hidden="true"></i>
                                <span class="layout-sidebar-rotulo">Ajuda</span>
                            </button>
                        </li>
                    }
                    @if (help.access.login.isAutenticado()) {
                        <li>
                            <button type="button" class="layout-sidebar-item" (click)="alterandoSenha.set(true)" (pointerenter)="entrarTela($event)" aria-label="Alterar senha" pTooltip="Alterar senha" tooltipPosition="right" [tooltipDisabled]="!dicas()">
                                <i class="layout-sidebar-icone pi pi-key" aria-hidden="true"></i>
                                <span class="layout-sidebar-rotulo">Alterar senha</span>
                            </button>
                        </li>
                    }
                    <li>
                        <button type="button" class="layout-sidebar-item" (click)="sair()" (pointerenter)="entrarTela($event)" aria-label="Sair" pTooltip="Sair" tooltipPosition="right" [tooltipDisabled]="!dicas()">
                            <i class="layout-sidebar-icone pi pi-sign-out" aria-hidden="true"></i>
                            <span class="layout-sidebar-rotulo">Sair</span>
                        </button>
                    </li>
                </ul>

                <div class="layout-sidebar-usuario" (pointerenter)="entrarTela($event)" [pTooltip]="descricaoUsuario()" tooltipPosition="right" [tooltipDisabled]="!dicas()">
                    <span class="layout-sidebar-avatar" aria-hidden="true">{{ iniciais() }}</span>
                    <span class="layout-sidebar-rotulo layout-sidebar-usuario-dados">
                        <span class="layout-sidebar-usuario-nome">{{ login.session()?.nomeUsuario }}</span>
                        <span class="layout-sidebar-usuario-email">{{ login.session()?.email }}</span>
                    </span>
                </div>

                <button
                    type="button"
                    role="switch"
                    class="layout-sidebar-tema"
                    [attr.aria-checked]="!!layoutService.isDarkTheme()"
                    aria-label="Modo escuro"
                    (click)="layoutService.toggleTheme()"
                    (pointerenter)="entrarTela($event)"
                    pTooltip="Modo escuro"
                    tooltipPosition="right"
                    [tooltipDisabled]="!dicas()"
                >
                    <span class="layout-sidebar-switch" aria-hidden="true"><span class="layout-sidebar-switch-bola"></span></span>
                    <span class="layout-sidebar-rotulo">Modo escuro</span>
                </button>
            </div>
        </aside>
        <app-alterar-senha [(visivel)]="alterandoSenha" />

        @if (!arvore() && painel(); as grupo) {
            <div id="layout-sidebar-painel" class="layout-sidebar-painel" role="region" [attr.aria-label]="grupo.rotulo" (pointerenter)="cancelarFechamento()" (pointerleave)="sairItem($event)">
                <div class="layout-sidebar-painel-cabecalho">
                    <span class="layout-sidebar-painel-icone" aria-hidden="true"><i [ngClass]="grupo.icone ?? 'pi pi-folder'"></i></span>
                    <div class="layout-sidebar-painel-titulo">{{ grupo.rotulo }}</div>
                </div>
                <ng-container *ngTemplateOutlet="subarvore; context: { $implicit: grupo.filhos }" />
            </div>
        }

        <ng-template #subarvore let-nos>
            <ul class="layout-sidebar-sublista">
                @for (no of nos; track no.chave) {
                    <li>
                        @if (no.filhos.length) {
                            <button
                                type="button"
                                class="layout-sidebar-subitem layout-sidebar-subgrupo"
                                [class.ativo]="contemAtivo(no)"
                                [class.aberto]="estaAberto(no)"
                                [attr.aria-expanded]="estaAberto(no)"
                                (click)="clicarSubgrupo(no)"
                                (pointerenter)="intencaoDeAbrir(no, $event)"
                                (pointerleave)="cancelarIntencao()"
                            >
                                <i class="layout-sidebar-subicone" [ngClass]="no.icone ?? 'pi pi-folder'" aria-hidden="true"></i>
                                <span class="layout-sidebar-subrotulo">{{ no.rotulo }}</span>
                                <i class="pi pi-angle-down layout-sidebar-seta" aria-hidden="true"></i>
                            </button>
                            @if (estaAberto(no)) {
                                <ng-container *ngTemplateOutlet="subarvore; context: { $implicit: no.filhos }" />
                            }
                        } @else {
                            <ng-container *ngTemplateOutlet="tela; context: { $implicit: no, raiz: false }" />
                        }
                    </li>
                }
            </ul>
        </ng-template>

        <ng-template #tela let-no let-raiz="raiz">
            @if (no.link) {
                <a
                    [routerLink]="no.link"
                    [ngClass]="raiz ? 'layout-sidebar-item' : 'layout-sidebar-subitem'"
                    [class.ativo]="chaveAtiva() === no.chave"
                    [attr.aria-current]="chaveAtiva() === no.chave ? 'page' : null"
                    [attr.aria-label]="raiz && !comRotulos() ? no.rotulo : null"
                    (click)="clicarTela(no)"
                    (pointerenter)="raiz && entrarTela($event)"
                    [pTooltip]="raiz ? no.rotulo : ''"
                    tooltipPosition="right"
                    [tooltipDisabled]="!raiz || !dicas()"
                >
                    <ng-container *ngTemplateOutlet="conteudoTela; context: { $implicit: no, raiz: raiz }" />
                </a>
            } @else {
                <button
                    type="button"
                    [ngClass]="raiz ? 'layout-sidebar-item' : 'layout-sidebar-subitem'"
                    [attr.aria-label]="raiz && !comRotulos() ? no.rotulo : null"
                    (click)="clicarTela(no)"
                    (pointerenter)="raiz && entrarTela($event)"
                    [pTooltip]="raiz ? no.rotulo : ''"
                    tooltipPosition="right"
                    [tooltipDisabled]="!raiz || !dicas()"
                >
                    <ng-container *ngTemplateOutlet="conteudoTela; context: { $implicit: no, raiz: raiz }" />
                </button>
            }
        </ng-template>

        <ng-template #conteudoTela let-no let-raiz="raiz">
            <i [ngClass]="[raiz ? 'layout-sidebar-icone' : 'layout-sidebar-subicone', no.icone ?? 'pi pi-circle']" aria-hidden="true"></i>
            <span [ngClass]="raiz ? 'layout-sidebar-rotulo' : 'layout-sidebar-subrotulo'">{{ no.rotulo }}</span>
        </ng-template>
    `
})
export class AppSidebar {
    readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
    readonly layoutService = inject(LayoutService);
    readonly help = inject(HelpService);
    readonly login = inject(LoginService);
    private readonly router = inject(Router);

    readonly nos: NoMenu[] = nosDaBarra(montarMenu(this.login.getTipo(), this.login.session()?.roles ?? [], this.help));

    private readonly url = signal(this.router.url);
    readonly chaveAtiva = computed(() => chaveDaUrl(this.nos, this.url()));

    readonly mobile = signal(false);
    /** Modo "abaixo": barra expandida (com rótulos) por cima do conteúdo. */
    readonly expandido = signal(false);
    /** Modo "lateral": grupo cujo painel está aberto ao lado da barra. */
    readonly painel = signal<NoMenu | null>(null);
    /** Grupos abertos no acordeão (modo "abaixo", mobile e seções do painel). */
    readonly abertos = signal<ReadonlySet<string>>(new Set(ancestrais(this.chaveAtiva())));

    /** No mobile a gaveta usa sempre o acordeão: não há hover nem espaço para um painel ao lado. */
    readonly arvore = computed(() => this.mobile() || this.layoutService.modoSubmenu() === 'abaixo');
    readonly comRotulos = computed(() => this.mobile() || (this.layoutService.modoSubmenu() === 'abaixo' && this.expandido()));
    /** Dicas só no modo lateral; no "abaixo" o hover já expande a barra com os rótulos. */
    readonly dicas = computed(() => !this.mobile() && this.layoutService.modoSubmenu() === 'lateral');

    readonly iniciais = computed(() => {
        const partes = (this.login.session()?.nomeUsuario ?? '').trim().split(/\s+/).filter(Boolean);
        return ((partes[0]?.[0] ?? '') + (partes.length > 1 ? partes[partes.length - 1][0] : '')).toUpperCase() || '?';
    });
    readonly descricaoUsuario = computed(() => [this.login.session()?.nomeUsuario, this.login.session()?.email].filter(Boolean).join(' · '));

    private timerBarra?: ReturnType<typeof setTimeout>;
    private timerIntencao?: ReturnType<typeof setTimeout>;
    private timerPainel?: ReturnType<typeof setTimeout>;
    private ultimoHover = { chave: '', quando: 0 };

    constructor() {
        const destroyRef = inject(DestroyRef);
        const media = window.matchMedia('(max-width: 991px)');
        const aoMudarTela = () => {
            this.mobile.set(media.matches);
            this.fecharPainel();
            this.recolher();
        };
        this.mobile.set(media.matches);
        media.addEventListener('change', aoMudarTela);
        destroyRef.onDestroy(() => {
            media.removeEventListener('change', aoMudarTela);
            [this.timerBarra, this.timerIntencao, this.timerPainel].forEach(timer => clearTimeout(timer));
        });

        this.router.events.pipe(filter(event => event instanceof NavigationEnd), takeUntilDestroyed()).subscribe(event => {
            this.url.set(event.urlAfterRedirects);
            this.fecharPainel();
            this.recolher();
        });
    }

    contemAtivo(no: NoMenu): boolean {
        return this.contemChaveAtiva(no.chave);
    }

    private contemChaveAtiva(chave: string): boolean {
        const ativa = this.chaveAtiva();
        return !!ativa && (ativa === chave || ativa.startsWith(chave + '-'));
    }

    estaAberto(no: NoMenu): boolean {
        return this.abertos().has(no.chave);
    }

    // --- Barra (modo "abaixo"): expande ao passar o mouse e recolhe ao sair

    aoEntrarNaBarra(event: PointerEvent): void {
        if (event.pointerType !== 'mouse' || !this.arvore() || this.mobile()) return;
        clearTimeout(this.timerBarra);
        // Expande com os grupos fechados: abrir o caminho da tela atual empurraria os itens que estão sob o mouse
        if (!this.expandido()) this.timerBarra = setTimeout(() => {
            this.abertos.set(new Set());
            this.expandido.set(true);
        }, ATRASO_EXPANDIR);
    }

    aoSairDaBarra(event: PointerEvent): void {
        if (event.pointerType !== 'mouse' || this.mobile()) return;
        clearTimeout(this.timerBarra);
        this.cancelarIntencao();
        if (this.expandido()) this.timerBarra = setTimeout(() => this.recolher(), ATRASO_RECOLHER);
    }

    aoFocarBarra(): void {
        if (this.mobile() || !this.arvore()) return;
        clearTimeout(this.timerBarra);
        this.expandido.set(true);
    }

    aoDesfocarBarra(event: FocusEvent): void {
        if (this.arvore() && !this.mobile() && !this.el.nativeElement.contains(event.relatedTarget as Node | null)) this.recolher();
    }

    /** Fecha a barra e deixa aberto só o caminho até a tela atual. */
    recolher(): void {
        clearTimeout(this.timerBarra);
        this.expandido.set(false);
        this.abertos.set(new Set(ancestrais(this.chaveAtiva())));
    }

    // --- Grupos

    entrarGrupo(no: NoMenu, event: PointerEvent): void {
        if (event.pointerType !== 'mouse') return;
        if (this.arvore()) return this.intencaoDeAbrir(no, event);
        clearTimeout(this.timerPainel);
        if (this.painel()?.chave !== no.chave) this.abrirPainel(no);
        this.ultimoHover = { chave: no.chave, quando: Date.now() };
    }

    clicarGrupo(no: NoMenu, event: MouseEvent): void {
        this.cancelarIntencao();
        no.acao?.();
        if (this.arvore()) {
            if (!this.mobile() && !this.expandido()) {
                // Toque ou teclado com a barra recolhida: expande já com o grupo aberto
                this.expandido.set(true);
                this.abrir(no);
                return;
            }
            return this.alternar(no);
        }
        const abertoAgoraPeloHover = this.ultimoHover.chave === no.chave && Date.now() - this.ultimoHover.quando < JANELA_CLIQUE_APOS_HOVER;
        if (this.painel()?.chave === no.chave && !abertoAgoraPeloHover) this.fecharPainel();
        else this.abrirPainel(no);
        // Pelo teclado (detail 0), leva o foco para o painel, que fica depois da barra na ordem de tabulação
        if (event.detail === 0) setTimeout(() => this.el.nativeElement.querySelector<HTMLElement>('#layout-sidebar-painel a, #layout-sidebar-painel button')?.focus());
    }

    clicarSubgrupo(no: NoMenu): void {
        no.acao?.();
        this.alternar(no);
    }

    private alternar(no: NoMenu): void {
        const abertoAgoraPeloHover = this.ultimoHover.chave === no.chave && Date.now() - this.ultimoHover.quando < JANELA_CLIQUE_APOS_HOVER;
        if (abertoAgoraPeloHover) return;
        this.cancelarIntencao();
        if (this.estaAberto(no)) this.abertos.update(abertos => new Set([...abertos].filter(chave => chave !== no.chave && !chave.startsWith(no.chave + '-'))));
        else this.abrir(no);
    }

    intencaoDeAbrir(no: NoMenu, event: PointerEvent): void {
        if (event.pointerType !== 'mouse' || !this.arvore() || this.mobile() || this.estaAberto(no)) return;
        this.cancelarIntencao();
        this.timerIntencao = setTimeout(() => {
            if (!this.expandido()) return;
            this.abrir(no);
            this.ultimoHover = { chave: no.chave, quando: Date.now() };
        }, ATRASO_INTENCAO);
    }

    cancelarIntencao(): void {
        clearTimeout(this.timerIntencao);
    }

    /** Abre o grupo e fecha os grupos vizinhos (mesmo pai), exceto o que contém a tela atual. */
    private abrir(no: NoMenu): void {
        const nivel = no.chave.split('-').length;
        const pai = no.chave.split('-').slice(0, -1).join('-');
        const deVizinhoFechavel = (chave: string) => {
            const partes = chave.split('-');
            if (partes.length < nivel) return false;
            const noNivel = partes.slice(0, nivel).join('-');
            return noNivel !== no.chave && partes.slice(0, nivel - 1).join('-') === pai && !this.contemChaveAtiva(noNivel);
        };
        this.abertos.update(abertos => new Set([...[...abertos].filter(chave => !deVizinhoFechavel(chave)), ...ancestrais(no.chave), no.chave]));
    }

    // --- Painel (modo "lateral")

    private abrirPainel(no: NoMenu): void {
        clearTimeout(this.timerPainel);
        this.abertos.set(new Set(ancestrais(this.chaveAtiva())));
        this.painel.set(no);
    }

    fecharPainel(): void {
        clearTimeout(this.timerPainel);
        this.painel.set(null);
    }

    cancelarFechamento(): void {
        clearTimeout(this.timerPainel);
    }

    sairItem(event: PointerEvent): void {
        if (event.pointerType !== 'mouse') return;
        this.cancelarIntencao();
        if (this.painel()) this.timerPainel = setTimeout(() => this.fecharPainel(), ATRASO_FECHAR_PAINEL);
    }

    /** Passar por uma tela da barra fecha o painel de outro grupo. */
    entrarTela(event: PointerEvent): void {
        if (event.pointerType === 'mouse') this.fecharPainel();
    }

    // --- Telas e ações

    clicarTela(no: NoMenu): void {
        no.acao?.();
        if (!no.link) this.fecharPainel();
    }

    /** Diálogo de troca da própria senha. */
    readonly alterandoSenha = signal(false);

    sair(): void {
        this.login.logout();
        this.router.navigate(['/auth/login']);
    }

    @HostListener('document:click', ['$event'])
    aoClicarFora(event: MouseEvent): void {
        if (this.el.nativeElement.contains(event.target as Node)) return;
        this.fecharPainel();
        if (this.expandido()) this.recolher();
    }

    @HostListener('document:keydown.escape')
    aoTeclarEsc(): void {
        const grupo = this.painel();
        if (grupo) {
            this.fecharPainel();
            const indice = this.nos.findIndex(no => no.chave === grupo.chave);
            this.el.nativeElement.querySelectorAll<HTMLElement>('.layout-sidebar-menu > ul > li')[indice]?.querySelector<HTMLElement>('button')?.focus();
        } else if (this.expandido()) {
            this.recolher();
        }
    }
}
